import orderBy from 'lodash-es/orderBy.js';
import ms from 'ms';
import { createElement, useState, useMemo } from 'react';
import { proxy } from 'valtio';
import { z } from 'zod';
import { gql } from '@soundxyz/gql-string';
import { QueryClient, QueryClientProvider, useQuery, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { filterUndefined, useStableCallback, useLatestRef, useStableObject, useProxySnapshot } from './utils.js';

const GraphQLExecutionResultSchema = z.object({
  data: z.record(z.unknown()).nullable().optional(),
  errors: z.array(
    z.object({
      message: z.string(),
      locations: z.array(z.object({ line: z.number(), column: z.number() })).optional(),
      path: z.array(z.union([z.string(), z.number()])).optional(),
      extensions: z.record(z.unknown()).optional()
    })
  ).optional(),
  extensions: z.record(z.unknown()).optional()
}).transform((value) => value);
function GraphQLReactQueryClient({
  clientConfig: clientConfigInput,
  endpoint,
  headers: headersGlobal,
  fetchOptions,
  fetcher: fetcherConfig,
  skipAbort
}) {
  var _a;
  const skipAbortSet = skipAbort ? skipAbort === true ? true : new Set(skipAbort) : null;
  const effectsStore = {};
  const uniqueFetches = {};
  const fetcher = fetcherConfig || async function Fetcher({ query, variables, fetchOptions: fetchOptions2 }) {
    const body = JSON.stringify({ query, variables });
    const headers = {
      "content-type": "application/json",
      ...fetchOptions2 == null ? void 0 : fetchOptions2.headers
    };
    const fetchKey = JSON.stringify({
      body,
      headersFetch: headers
    });
    const res = await (uniqueFetches[fetchKey] || (uniqueFetches[fetchKey] = fetch(
      endpoint,
      {
        method: "POST",
        body,
        ...fetchOptions2,
        headers
      }
    ).then((response) => {
      return response.json().then((value) => ({ response, json: { ok: true, value } })).catch((error) => ({ response, json: { ok: false, error } }));
    }))).catch((cause) => {
      console.error(cause);
      throw Error("Network error, fetch failed", {
        cause
      });
    }).finally(() => {
      uniqueFetches[fetchKey] = null;
    });
    if (!res.json.ok) {
      console.error(res.response);
      console.error(res.json.error);
      throw Error(
        `Network error, unexpected payload. Response status code ${res.response.status}`,
        {
          cause: res.json.error
        }
      );
    }
    const responseJson = GraphQLExecutionResultSchema.safeParse(res.json.value);
    if (responseJson.success)
      return responseJson.data;
    throw Error(`Unexpected API payload`, {
      cause: responseJson.error
    });
  };
  async function GQLFetcher({
    query,
    variables,
    fetchOptions: extraFetchOptions
  }) {
    const {
      data = null,
      errors,
      extensions
    } = await GraphQLReactQuery.fetcher({
      query,
      variables,
      fetchOptions: {
        ...fetchOptions,
        ...extraFetchOptions,
        headers: filterUndefined({
          ...headersGlobal,
          ...fetchOptions == null ? void 0 : fetchOptions.headers,
          ...extraFetchOptions == null ? void 0 : extraFetchOptions.headers
        })
      }
    });
    if (!data) {
      if (errors == null ? void 0 : errors.length) {
        if (errors.length > 1) {
          const err = Error("Multiple Errors", {
            cause: {
              errors
            }
          });
          for (const err2 of errors) {
            console.error(err2);
          }
          Object.assign(err, {
            graphqlErrors: errors
          });
          throw err;
        }
        const { message } = errors[0];
        throw new Error(message, {
          cause: {
            query,
            variables
          }
        });
      }
      throw Error("Unexpected missing data", {
        cause: {
          query,
          variables
        }
      });
    }
    const effects = effectsStore[query];
    if (effects) {
      for (const effect of effects) {
        try {
          Promise.all([
            effect({
              operation: query,
              result: { errors, data, extensions },
              variables
            })
          ]).catch(() => null);
        } catch (err) {
        }
      }
    }
    const result = {
      data
    };
    if (errors)
      result.errors = errors;
    if (extensions)
      result.extensions = extensions;
    return result;
  }
  const Effects = {
    /**
     * Add an effect callback to be called every time the specified operation request has been completed
     *
     * It returns a callback that's going to stop the effect from being called
     *
     * @example
     * Effects.onCompleted(TestQuery, ({ operation, result: { data }, variables }) => {
     *  console.log({
     *    operation,
     *    data,
     *    variables
     *  });
     * });
     */
    onCompleted(operation, callback) {
      const effects = effectsStore[operation] || (effectsStore[operation] = /* @__PURE__ */ new Set());
      effects.add(callback);
      return function removeEffect() {
        effects.delete(callback);
        if (effects.size === 0)
          effectsStore[operation] = null;
      };
    }
  };
  const defaultQueryFn = ({ queryKey, signal }) => {
    const [query, variables] = queryKey;
    if (typeof query !== "string")
      throw Error(`Invalid GraphQL operation given`);
    return GQLFetcher({
      query,
      variables: (
        // Impossible to validate generic types dynamically
        variables
      ),
      fetchOptions: skipAbortSet === true || (skipAbortSet == null ? void 0 : skipAbortSet.has(query)) ? void 0 : {
        signal
      }
    });
  };
  const queryFnWithFetchOptions = (fetchOptions2) => ({ queryKey, signal }) => {
    const [query, variables] = queryKey;
    if (typeof query !== "string")
      throw Error(`Invalid GraphQL operation given`);
    return GQLFetcher({
      query,
      variables,
      fetchOptions: {
        signal: skipAbortSet === true || (skipAbortSet == null ? void 0 : skipAbortSet.has(query)) ? void 0 : signal,
        ...fetchOptions2
      }
    });
  };
  const clientConfig = {
    ...clientConfigInput,
    defaultOptions: {
      ...clientConfigInput == null ? void 0 : clientConfigInput.defaultOptions,
      queries: {
        queryFn: defaultQueryFn,
        ...(_a = clientConfigInput == null ? void 0 : clientConfigInput.defaultOptions) == null ? void 0 : _a.queries
      }
    }
  };
  const client = new QueryClient(clientConfig);
  function GraphQLReactQueryProvider({ children }) {
    return createElement(QueryClientProvider, { client, children });
  }
  function fetchGQL(query, {
    variables,
    ...fetchOptions2
  }) {
    return GQLFetcher({
      query,
      variables,
      fetchOptions: fetchOptions2
    });
  }
  function setQueryData({
    query,
    options,
    variables,
    filterQueryKey
  }, updater) {
    client.setQueryData(
      filterQueryKey !== void 0 ? [query, variables, filterQueryKey] : [query, variables],
      updater,
      options
    );
  }
  function getTimeProp(time) {
    return typeof time === "number" ? time : ms(time);
  }
  function useQuery$1(query, {
    variables,
    enabled = true,
    fetchOptions: fetchOptions2,
    filterQueryKey,
    staleTime,
    cacheTime,
    ...options
  }) {
    const result = useQuery({
      queryFn: fetchOptions2 ? queryFnWithFetchOptions(fetchOptions2) : defaultQueryFn,
      queryKey: filterQueryKey !== void 0 ? [query, variables, filterQueryKey] : [query, variables],
      ...options,
      enabled: enabled && variables !== false,
      staleTime: getTimeProp(staleTime),
      cacheTime: cacheTime != null ? getTimeProp(cacheTime) : void 0
    });
    const setQueryDataCallback = useStableCallback(
      (updater, options2) => {
        if (variables === false)
          return;
        setQueryData(
          {
            query,
            // Not able to map required variables generic conditional
            variables,
            filterQueryKey,
            options: options2
          },
          updater
        );
      }
    );
    return {
      ...result,
      isLoading: result.isInitialLoading,
      setQueryData: setQueryDataCallback
    };
  }
  function fetchQuery(query, {
    variables,
    fetchOptions: fetchOptions2,
    filterQueryKey,
    ...options
  }) {
    return client.fetchQuery({
      queryKey: filterQueryKey !== void 0 ? [query, variables, filterQueryKey] : [query, variables],
      queryFn: fetchOptions2 ? queryFnWithFetchOptions(fetchOptions2) : defaultQueryFn,
      ...options
    });
  }
  function prefetchQuery(query, {
    variables,
    filterQueryKey,
    ...options
  }) {
    return client.prefetchQuery({
      queryKey: filterQueryKey !== void 0 ? [query, variables, filterQueryKey] : [query, variables],
      ...options
    });
  }
  const infiniteQueryStores = {};
  async function infiniteQueryFn({
    query,
    variables,
    list,
    uniq,
    onFetchCompleted,
    signal,
    entityStoreNodes
  }) {
    const response = await GQLFetcher({
      query,
      variables,
      fetchOptions: skipAbortSet === true || (skipAbortSet == null ? void 0 : skipAbortSet.has(query)) ? void 0 : {
        signal
      }
    });
    try {
      for (const node of list(response.data) || []) {
        const key = uniq(node);
        entityStoreNodes[key] = node;
      }
    } catch (cause) {
      throw Error("Internal server error. Unexpected payload", {
        cause
      });
    }
    if (onFetchCompleted) {
      try {
        Promise.all([onFetchCompleted(response)]).catch(() => null);
      } catch (err) {
      }
    }
    return response;
  }
  function setInfiniteQueryData({
    query,
    filterQueryKey,
    options
  }, updater) {
    const queryKey = infiniteQueryKey({
      query,
      variables: true,
      filterQueryKey
    });
    client.setQueryData(queryKey, updater, options);
  }
  function infiniteQueryKey({
    query,
    filterQueryKey,
    variables
  }) {
    return [query, filterQueryKey, !!variables, "Infinite"];
  }
  function useInfiniteQuery$1(query, {
    variables,
    filterQueryKey = {},
    list,
    uniq,
    order,
    onFetchCompleted,
    staleTime,
    cacheTime,
    enabled = true,
    ...options
  }) {
    var _a2;
    const entityStore = infiniteQueryStores[_a2 = query + JSON.stringify(filterQueryKey)] || (infiniteQueryStores[_a2] = proxy({
      nodes: {}
    }));
    const entityStoreNodes = entityStore.nodes;
    const queryKey = infiniteQueryKey({
      query,
      filterQueryKey,
      variables
    });
    const setInfiniteQueryDataCallback = useStableCallback(
      (updater, options2) => {
        setInfiniteQueryData(
          {
            query,
            filterQueryKey,
            options: options2
          },
          updater
        );
      }
    );
    const [latestData, setLatestData] = useState(
      null
    );
    const result = useInfiniteQuery({
      staleTime: getTimeProp(staleTime),
      cacheTime: cacheTime != null ? getTimeProp(cacheTime) : void 0,
      queryKey,
      queryFn: variables ? async ({ pageParam, signal }) => {
        const result2 = await infiniteQueryFn({
          query,
          variables: variables({ pageParam: pageParam || null }),
          list,
          uniq,
          onFetchCompleted,
          signal,
          entityStoreNodes
        });
        setLatestData(result2);
        return result2;
      } : () => {
        throw Error(`Missing variables required to execute query!`);
      },
      ...options,
      enabled: enabled && !!variables
    });
    const {
      data,
      hasNextPage,
      hasPreviousPage,
      fetchNextPage,
      fetchPreviousPage,
      isFetchingNextPage,
      isFetchingPreviousPage
    } = result;
    const loadMoreNextPage = useStableCallback(() => {
      if (hasNextPage && !isFetchingNextPage)
        return fetchNextPage();
      return null;
    });
    const loadMorePreviousPage = useStableCallback(() => {
      if (hasPreviousPage && !isFetchingPreviousPage)
        return fetchPreviousPage();
      return null;
    });
    const firstPage = data == null ? void 0 : data.pages[0];
    const lastPage = data == null ? void 0 : data.pages[result.data.pages.length - 1];
    const latestOrder = useLatestRef(order == null ? void 0 : order[0]);
    const latestListFn = useLatestRef(list);
    const latestUniq = useLatestRef(uniq);
    const stableOrderType = useStableObject(order == null ? void 0 : order[1]);
    const { nodes: entityStoreNodesSnapshot } = useProxySnapshot(entityStore);
    const orderedList = useMemo(() => {
      if (!data)
        return [];
      const currentListFn = latestListFn.current;
      const currentUniq = latestUniq.current;
      const currentOrder = latestOrder.current;
      const values = data.pages.reduce((acc, page) => {
        const listValues = page.data ? currentListFn(page.data) || [] : [];
        for (const entity of listValues) {
          const key = currentUniq(entity);
          acc[key] = entityStoreNodesSnapshot[key] || entity;
        }
        return acc;
      }, {});
      if (currentOrder)
        return orderBy(values, currentOrder, stableOrderType);
      return Object.values(values);
    }, [stableOrderType, data, entityStoreNodesSnapshot]);
    return {
      ...result,
      isLoadingNewPage: result.isInitialLoading || result.isFetchingNextPage || result.isFetchingPreviousPage,
      isLoading: result.isInitialLoading,
      firstPage,
      lastPage,
      orderedList,
      loadMoreNextPage,
      loadMorePreviousPage,
      entityStore,
      setInfiniteQueryData: setInfiniteQueryDataCallback,
      latestData
    };
  }
  function prefetchInfiniteQuery(query, {
    filterQueryKey,
    variables,
    list,
    uniq,
    onFetchCompleted,
    ...options
  }) {
    var _a2;
    const { nodes: entityStoreNodes } = infiniteQueryStores[_a2 = query + JSON.stringify(filterQueryKey)] || (infiniteQueryStores[_a2] = proxy({
      nodes: {}
    }));
    return client.prefetchInfiniteQuery({
      queryKey: [query, filterQueryKey, variables, "Infinite"],
      queryFn({ signal }) {
        return infiniteQueryFn({
          query,
          variables,
          list,
          uniq,
          signal,
          entityStoreNodes,
          onFetchCompleted
        });
      },
      ...options
    });
  }
  function useMutation$1(mutation, options) {
    return useMutation({
      mutationFn(variables) {
        return GQLFetcher({
          query: mutation,
          variables
        });
      },
      ...options
    });
  }
  async function invalidateOperations({
    filters,
    operations,
    options
  }) {
    await Promise.all(
      operations.map(
        (operation) => client.invalidateQueries(
          {
            ...filters,
            queryKey: [operation]
          },
          options
        )
      )
    );
  }
  async function resetOperations({
    filters,
    operations,
    options
  }) {
    await Promise.all(
      operations.map(
        (operation) => client.resetQueries(
          {
            ...filters,
            queryKey: [operation]
          },
          options
        )
      )
    );
  }
  const GraphQLReactQuery = {
    client,
    clientConfig,
    GraphQLReactQueryProvider,
    useQuery: useQuery$1,
    prefetchQuery,
    setQueryData,
    fetcher,
    fetchQuery,
    useMutation: useMutation$1,
    fetchGQL,
    useInfiniteQuery: useInfiniteQuery$1,
    prefetchInfiniteQuery,
    setInfiniteQueryData,
    gql,
    invalidateOperations,
    resetOperations,
    Effects
  };
  return GraphQLReactQuery;
}

export { GraphQLExecutionResultSchema, GraphQLReactQueryClient };
